import React, { useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import useFetchPaginatedConnectionRuntimeConfig from "../../hooks/useFetchPaginatedConnectionRuntimeConfig";
import { getFormattedDateTime } from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconDeleteBlueSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import {
  deleteConnectionRuntimeConfig,
  reactivateConnectionRuntimeConfig,
} from "../../services/connectionRuntimeConfig";
import { getConfig } from "../../config/runtimeConfig";

const ConnectionRuntimeConfigList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>([]);
  const [searchFilterData, setSearchFilterData] = useState<any>({
    connection_runtime_id: searchParams.get("filter_id") || "",
    code: searchParams.get("code") || "",
    connection_type: searchParams.get("connection_type") || "",
    status_filter: searchParams.get("status") || "Active", // Default to Active status
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [configToDelete, setConfigToDelete] = useState<any>(null);

  // Determine if we should include filter ID in API call
  const effectiveFilterId =
    searchFilterData.connection_runtime_id?.trim().length >= 1
      ? searchFilterData.connection_runtime_id
      : "";

  // Determine code filter value
  const effectiveCode = searchFilterData.code?.trim() || "";

  // Determine connection_type filter value
  const effectiveConnectionType =
    searchFilterData.connection_type?.trim() || "";

  // Determine status filter value
  const effectiveStatusFilter = searchFilterData.status_filter || "Active";

  // Convert status_filter to isActive parameter
  let isActiveParam = "true"; // Default to active
  if (effectiveStatusFilter === "Inactive") {
    isActiveParam = "false";
  } else if (effectiveStatusFilter === "All") {
    isActiveParam = "all";
  }

  // hooks
  const [fetchedConnectionRuntimeConfig] =
    useFetchPaginatedConnectionRuntimeConfig({
      setIsLoading,
      page,
      pSize,
      filterId: effectiveFilterId || undefined,
      isActive: isActiveParam,
      code: effectiveCode || undefined,
      connection_type: effectiveConnectionType || undefined,
    });

  // Reset page to 1 when search filter changes
  useEffect(() => {
    setPage(1);
  }, [
    searchFilterData.connection_runtime_id,
    searchFilterData.code,
    searchFilterData.connection_type,
    searchFilterData.status_filter,
  ]);

  // Update URL search params when search filter changes
  useEffect(() => {
    const currentSearch = searchParams.get("filter_id") || "";
    const currentCode = searchParams.get("code") || "";
    const currentConnectionType = searchParams.get("connection_type") || "";
    const currentStatus = searchParams.get("status") || "Active";
    const newSearch = searchFilterData.connection_runtime_id || "";
    const newCode = searchFilterData.code || "";
    const newConnectionType = searchFilterData.connection_type || "";
    const newStatus = searchFilterData.status_filter || "Active";

    if (
      newSearch !== currentSearch ||
      newCode !== currentCode ||
      newConnectionType !== currentConnectionType ||
      newStatus !== currentStatus
    ) {
      const newParams = new URLSearchParams();

      // Add filter_id if present
      if (newSearch.trim()) {
        newParams.set("filter_id", newSearch.trim());
      }

      // Add code if present
      if (newCode.trim()) {
        newParams.set("code", newCode.trim());
      }

      // Add connection_type if present
      if (newConnectionType.trim()) {
        newParams.set("connection_type", newConnectionType.trim());
      }

      // Always add status (default to Active)
      newParams.set("status", newStatus);

      setSearchParams(newParams);
    }
  }, [
    searchFilterData.connection_runtime_id,
    searchFilterData.code,
    searchFilterData.connection_type,
    searchFilterData.status_filter,
    searchParams,
    setSearchParams,
  ]);

  // Update search filter when URL search params change (for back/forward navigation)
  useEffect(() => {
    const urlSearch = searchParams.get("filter_id") || "";
    const urlCode = searchParams.get("code") || "";
    const urlConnectionType = searchParams.get("connection_type") || "";
    const urlStatus = searchParams.get("status") || "Active";

    if (
      searchFilterData.connection_runtime_id !== urlSearch ||
      searchFilterData.code !== urlCode ||
      searchFilterData.connection_type !== urlConnectionType ||
      searchFilterData.status_filter !== urlStatus
    ) {
      setSearchFilterData((prev: any) => ({
        ...prev,
        connection_runtime_id: urlSearch,
        code: urlCode,
        connection_type: urlConnectionType,
        status_filter: urlStatus,
      }));
    }
  }, [searchParams]);

  const handelClickEvent = () => {
    navigate("/connection-runtime-config/add");
  };

  // Handle delete connection runtime config
  const handleDelete = (config: any) => {
    setConfigToDelete(config);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!configToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteConnectionRuntimeConfig(configToDelete.id);
      if (result && result.status === 200) {
        showToast(`Connection Runtime Config deleted successfully!`, "success");
        setDeleteConfirmOpen(false);
        setConfigToDelete(null);
        // Remove the deleted item from the list
        const updatedItems = fileData?.items?.filter(
          (item: any) => item.id !== configToDelete.id
        );
        setFileData((prev: any) => ({
          ...prev,
          items: updatedItems,
        }));
      }
    } catch (error: any) {
      showToast(
        error?.message || "Failed to delete connection runtime config",
        "error"
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate connection runtime config
  const handleReactivate = async (id: number, code: string) => {
    setIsLoading(true);
    try {
      await reactivateConnectionRuntimeConfig(id);
      showToast(
        `Connection Runtime Config "${code}" reactivated successfully!`,
        "success"
      );

      // Remove the reactivated item from the list (since it's no longer inactive)
      const updatedItems = fileData?.items?.filter(
        (item: any) => item.id !== id
      );
      setFileData((prev: any) => ({
        ...prev,
        items: updatedItems,
      }));
    } catch (error) {
      showToast("Error reactivating connection runtime config", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setFileData(fetchedConnectionRuntimeConfig);
  }, [fetchedConnectionRuntimeConfig]);

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: (params: any) => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "ID",
      minWidth: 80,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "connection_type",
      headerName: "Connection Type",
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "code",
      headerName: "Code",
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title="Active" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      width: 180,
      renderCell: (params: any) => {
        const isActive = params.row.is_active !== false; // Default to true if undefined

        return (
          <>
            {/* For active records, show View, Edit, and Delete buttons */}
            {isActive && (
              <>
                <Tooltip title="View" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(
                        `/connection-runtime-config/view/${params.row.id}`
                      );
                    }}
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(
                        `/connection-runtime-config/edit/${params.row.id}`
                      );
                    }}
                  >
                    <IconEditBase />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleDelete(params.row)}
                  >
                    <IconDeleteBlueSvg />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && (
              <Tooltip
                title="Reactivate Connection Runtime Config"
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.code)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Connection Runtime Config"
        dailogDescription={`Are you sure you want to delete connection runtime config "${configToDelete?.code}"?`}
      />
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className="text-box-card compact-text-box-card list-page-card"
      >
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <FlexBetween sx={{ columnGap: 2, marginBottom: 2 }}>
            <Box sx={{ flex: 1 }}>
              <SearchFilterBar
                className="horizontal-filter-search"
                largeWidth={true}
                setSearchFilterData={setSearchFilterData}
                searchFilterData={searchFilterData}
                requiredFields={[
                  "connection_runtime_id",
                  "status_filter",
                  "code",
                  "connection_type",
                ]}
                primaryFields={["connection_runtime_id", "status_filter"]}
                fieldRequirements={{
                  defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              className="btn-orange"
              onClick={handelClickEvent}
              startIcon={<AddSharpIcon />}
            >
              Connection runtime config
            </Button>
          </FlexBetween>
        </Grid>
      </Grid>

      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={"Connection Runtime Config List"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default ConnectionRuntimeConfigList;
